import { useState } from 'react';
import { ChevronDown, ChevronUp, Shield, FileText, AlertCircle, 
         Users, CreditCard, Bot, Lock, Phone, Mail, MapPin } from 'lucide-react';

function TermsAndConditions() {
  const [openSection, setOpenSection] = useState(null);

  const toggle = (id) => setOpenSection(openSection === id ? null : id);

  const lastUpdated = "May 2025";
  const companyName = "ChatTech24";
  const companyEmail = "legal@chattech24.com";
  const grievanceEmail = "grievance@chattech24.com";
  const companyAddress = "Your Company Address, City, State, PIN - India";
  const grievanceName = "Your Grievance Officer Name";

  const sections = [
    {
      id: 1,
      icon: <FileText size={20} />,
      title: "1. Introduction & Acceptance of Terms",
      content: `Welcome to ${companyName} ("Platform", "we", "us", "our"). 
      
By accessing or using our services at chattech24.com, you ("User", "you", "your") agree to be bound by these Terms and Conditions ("Terms"), our Privacy Policy, and all applicable laws and regulations.

If you do not agree with any part of these Terms, you must immediately discontinue use of our Platform.

These Terms were last updated in ${lastUpdated} and supersede all previous versions. We reserve the right to modify these Terms at any time. Continued use of the Platform after changes constitutes acceptance of the new Terms.

These Terms apply to:
- Individual users creating personal accounts
- Business users and organizations using our WhatsApp automation services
- Administrators managing organizational accounts`
    },
    {
      id: 2,
      icon: <FileText size={20} />,
      title: "2. Definitions",
      content: `For the purpose of these Terms:

- "Platform" refers to ChatTech24 website, web application, APIs, and all related services
- "User" means any individual or entity that accesses or uses our Platform
- "Organization" means a business entity registered on our Platform
- "WhatsApp API" refers to Meta's WhatsApp Business API integrated into our Platform
- "AI Agent" refers to the OpenAI-powered chatbot feature available on select plans
- "Flow" refers to automated WhatsApp conversation sequences created using our Flow Builder
- "Subscription" means a paid plan granting access to Platform features
- "Content" means any data, messages, files, or information uploaded to or transmitted through the Platform
- "DPDP Act" means the Digital Personal Data Protection Act, 2023 of India
- "Personal Data" means any data that can identify a natural person, as defined under the DPDP Act`
    },
    {
      id: 3,
      icon: <Phone size={20} />,
      title: "3. Service Description",
      content: `ChatTech24 provides a WhatsApp Business automation SaaS platform offering:

3.1 Core Services:
- WhatsApp Business API integration and management
- Visual Flow Builder for creating automated conversation sequences
- AI-powered chatbot (on eligible subscription plans)
- Contact management and messaging history
- Analytics and reporting dashboard
- Media Library for AI Agent file management

3.2 Service Tiers:
- Flow Only Plans: WhatsApp automation without AI capabilities
- AI + Flow Plans: Full automation with GPT-powered AI Agent
- Managed Setup Plans: Our team handles your WhatsApp API configuration

3.3 Service Availability:
- We target 99% uptime but do not guarantee uninterrupted service
- Maintenance windows will be communicated in advance where possible
- Availability is subject to Meta's WhatsApp API availability

3.4 Third-Party Dependencies:
Our Platform integrates with:
- Meta (WhatsApp Business API) — subject to Meta's Terms of Service
- OpenAI (AI Agent feature) — subject to OpenAI's usage policies
- Payment processors — subject to their respective terms`
    },
    {
      id: 4,
      icon: <Users size={20} />,
      title: "4. Eligibility & Account Registration",
      content: `4.1 Eligibility Requirements:
- You must be at least 18 years of age
- You must have legal authority to enter into binding contracts
- For organizational accounts: You must have authority to represent the business
- You must not be barred from receiving services under applicable Indian law

4.2 Account Registration:
- You must provide accurate, complete, and current information during registration
- You are responsible for maintaining the confidentiality of your account credentials
- You must notify us immediately of any unauthorized access at ${companyEmail}
- One person or entity may not maintain more than one active account without prior written consent

4.3 Account Responsibility:
- You are fully responsible for all activities conducted under your account
- You must not share login credentials with unauthorized persons
- We reserve the right to terminate accounts suspected of fraudulent activity

4.4 WhatsApp Business Account:
- To use WhatsApp features, you must comply with Meta's WhatsApp Business Policy
- You must have legitimate ownership or authorization over the WhatsApp number you connect
- Connecting a number without proper authorization is strictly prohibited`
    },
    {
      id: 5,
      icon: <CreditCard size={20} />,
      title: "5. Subscription, Payments & Refunds",
      content: `5.1 Subscription Plans:
- Our services are offered on a subscription basis with various plan tiers
- Plan features, pricing, and limitations are detailed on our Pricing page
- Plans are subject to change with 30 days' notice to existing subscribers

5.2 Payment Terms:
- All prices are listed in Indian Rupees (INR) and include applicable taxes
- Subscriptions are billed on the frequency specified at purchase (monthly/annual)
- Payment is due at the beginning of each billing cycle
- We use secure third-party payment processors — we do not store your card details

5.3 Auto-Renewal:
- Subscriptions automatically renew unless cancelled before the renewal date
- You will receive a reminder email 7 days before renewal
- You can cancel auto-renewal from your account settings at any time

5.4 Refund Policy:
- Subscription fees are generally non-refundable once the billing cycle has started
- Exceptions may be made at our sole discretion for documented technical failures on our part
- Refund requests must be submitted within 7 days of the charge to ${companyEmail}
- Setup fees for managed plans are non-refundable once setup has commenced

5.5 Suspension for Non-Payment:
- Accounts with failed payments will be suspended after 3 days
- WhatsApp integration will be deactivated upon subscription expiry
- Data is retained for 30 days after expiry before permanent deletion

5.6 GST & Taxes:
- Prices may be subject to GST as per applicable Indian tax laws
- You are responsible for any additional taxes applicable in your jurisdiction`
    },
    {
      id: 6,
      icon: <Phone size={20} />,
      title: "6. WhatsApp API Usage & Meta Compliance",
      content: `6.1 Meta's Policies:
- Use of WhatsApp features is subject to Meta's WhatsApp Business Policy and Terms of Service
- You agree to comply with Meta's policies at all times
- We are not responsible for actions taken by Meta against your WhatsApp Business Account

6.2 Permitted Use:
- Customer support and service notifications
- Order confirmations, shipping updates, and transactional messages
- Appointment reminders and booking confirmations
- Opt-in marketing to users who have explicitly consented

6.3 Strictly Prohibited:
- Sending spam, bulk promotional messages to non-consenting users
- Sending messages to numbers that have not opted in
- Distributing illegal content, hate speech, or harmful material
- Automated messaging that violates WhatsApp's rate limits
- Using the Platform for any illegal commercial activity
- Misrepresenting your identity or business

6.4 Message Quality:
- You are solely responsible for the content of all messages sent through the Platform
- We reserve the right to suspend accounts receiving high spam reports
- Repeated policy violations may result in permanent account termination and reporting to Meta

6.5 Phone Number Ownership:
- You warrant that you have full legal right to use the WhatsApp number connected to our Platform
- Each WhatsApp number can only be connected to one organization on our Platform
- Attempting to use another organization's number is a serious breach of these Terms`
    },
    {
      id: 7,
      icon: <Bot size={20} />,
      title: "7. AI Agent Usage & OpenAI Integration",
      content: `7.1 AI Agent Feature:
- The AI Agent feature is powered by OpenAI's language models
- This feature is available only on eligible subscription plans
- AI responses are generated automatically and may not always be accurate

7.2 User Responsibility for AI:
- You are responsible for configuring appropriate business prompts for your AI Agent
- You are fully liable for all AI-generated responses sent to your customers
- You must review and test AI responses before deploying in production
- We strongly recommend having a human review process for sensitive communications

7.3 AI Limitations:
- AI responses may contain errors, inaccuracies, or inappropriate content
- We do not warrant the accuracy, completeness, or appropriateness of AI responses
- The AI Agent should not be used for medical, legal, or financial advice

7.4 API Keys (Self-Managed Plans):
- If you provide your own OpenAI API key, you are responsible for its security
- We encrypt stored API keys but cannot be held liable for unauthorized access resulting from your negligence
- You must comply with OpenAI's usage policies when using your own API key

7.5 Managed AI Credits (Managed Plans):
- AI credits included in managed plans are for use on our Platform only
- Credits are non-transferable and expire at the end of each billing cycle
- Excessive usage beyond plan limits may result in temporary throttling

7.6 AI Content Policy:
- The AI Agent must not be configured to produce illegal, harmful, or deceptive content
- Configuring AI for purposes that violate Meta's policies is prohibited
- We reserve the right to disable AI features if misuse is detected`
    },
    {
      id: 8,
      icon: <Lock size={20} />,
      title: "8. Data Privacy & DPDP Act 2023 Compliance",
      content: `ChatTech24 is committed to protecting your personal data in accordance with the Digital Personal Data Protection Act, 2023 (DPDP Act) of India.

8.1 Data We Collect:
Account Data: Name, email address, phone number, organization details
WhatsApp Data: Phone numbers of your customers, message content, conversation history
Usage Data: Login times, features used, IP addresses, browser information
Payment Data: Transaction IDs, billing address (card details processed by payment gateway only)
AI Data: Business prompts you configure, AI conversation logs
Media: Files uploaded to AI Media Library

8.2 Purpose of Data Collection:
- Providing and improving our services
- Processing payments and managing subscriptions
- Sending service notifications and updates
- Complying with legal obligations
- Preventing fraud and ensuring platform security
- Analytics to improve user experience

8.3 Data Retention:
- Active account data: Retained for the duration of your subscription
- After account deletion: Data purged within 30 days
- Payment records: Retained for 7 years as required by Indian tax law
- Backup copies: May persist for up to 90 days after deletion request

8.4 Your Rights Under DPDP Act 2023:
- Right to Access: Request a copy of your personal data
- Right to Correction: Request correction of inaccurate data
- Right to Erasure: Request deletion of your data (subject to legal retention requirements)
- Right to Grievance Redressal: File complaints with our Grievance Officer
- Right to Nominate: Nominate another person to exercise rights on your behalf
- Right to Withdraw Consent: Withdraw consent for data processing (may affect service availability)

8.5 To Exercise Your Rights:
Contact our Grievance Officer at: ${grievanceEmail}
Response time: Within 72 hours as required by DPDP Act

8.6 Data Sharing:
We share data with:
- Meta (WhatsApp API processing) — only what is necessary for API functionality
- OpenAI (AI Agent processing) — conversation context only
- Payment processors — billing information only
- Legal authorities — when required by law

We do NOT sell your personal data to third parties.

8.7 Data Security:
- All data transmitted over SSL/TLS encryption
- API keys and sensitive credentials are encrypted at rest
- Regular security audits conducted
- Access to data is restricted to authorized personnel only

8.8 Cross-Border Data Transfer:
- Some data may be processed by servers outside India (OpenAI, Meta)
- Such transfers are subject to appropriate safeguards as required by DPDP Act

8.9 Children's Data:
- Our Platform is not intended for users under 18 years of age
- We do not knowingly collect data from minors
- If you believe a minor has provided us data, contact us immediately`
    },
    {
      id: 9,
      icon: <AlertCircle size={20} />,
      title: "9. User Obligations & Prohibited Uses",
      content: `9.1 You agree to:
- Use the Platform only for lawful business purposes
- Provide accurate information at all times
- Obtain necessary consents from your customers before sending them messages
- Comply with all applicable Indian laws including IT Act 2000, TRAI regulations
- Maintain the security of your account credentials

9.2 Prohibited Activities:
You must NOT use our Platform to:
- Send unsolicited commercial communications (spam)
- Engage in phishing, fraud, or deceptive practices
- Distribute malware, viruses, or harmful code
- Violate intellectual property rights of any party
- Harass, threaten, or abuse any person
- Engage in political campaigning without required disclosures
- Conduct illegal gambling, pyramid schemes, or MLM scams
- Promote violence, terrorism, or illegal substances
- Circumvent any Platform security measures
- Scrape or harvest data from the Platform
- Resell Platform access without written authorization

9.3 Consequences of Violation:
- Immediate account suspension without refund
- Permanent ban from the Platform
- Reporting to relevant authorities where legally required
- Legal action for damages caused to us or third parties`
    },
    {
      id: 10,
      icon: <Shield size={20} />,
      title: "10. Intellectual Property",
      content: `10.1 Our Intellectual Property:
- ChatTech24, its logo, platform design, and all software are our exclusive intellectual property
- You are granted a limited, non-exclusive, non-transferable license to use the Platform
- This license is solely for your internal business use during your active subscription
- You may not copy, modify, distribute, or reverse-engineer any part of our Platform

10.2 Your Content:
- You retain ownership of all content you upload or create on the Platform
- By using the Platform, you grant us a limited license to process your content to provide the service
- This license terminates when you delete your account

10.3 Feedback:
- Any suggestions or feedback you provide may be used by us to improve the Platform
- You grant us unlimited rights to use such feedback without compensation

10.4 Trademarks:
- "ChatTech24" is our trademark. Unauthorized use is prohibited.
- WhatsApp® is a trademark of Meta Platforms Inc. We are not affiliated with Meta.
- OpenAI® is a trademark of OpenAI LLC. We are not affiliated with OpenAI.`
    },
    {
      id: 11,
      icon: <AlertCircle size={20} />,
      title: "11. Disclaimers & Limitation of Liability",
      content: `11.1 Service Disclaimer:
- The Platform is provided "AS IS" and "AS AVAILABLE" without warranties of any kind
- We do not warrant uninterrupted, error-free, or secure operation of the Platform
- We are not responsible for actions taken by Meta against your WhatsApp Business Account
- We are not liable for AI-generated content that is inaccurate, inappropriate, or harmful

11.2 Limitation of Liability:
- Our total liability to you for any claims shall not exceed the amount paid by you in the 3 months preceding the claim
- We are not liable for: loss of profits, loss of data, business interruption, indirect or consequential damages
- This limitation applies even if we have been advised of the possibility of such damages

11.3 Force Majeure:
- We are not liable for failures caused by events beyond our reasonable control including: natural disasters, government actions, Meta/OpenAI service outages, internet failures, or cyberattacks

11.4 Third-Party Services:
- We are not responsible for the availability, accuracy, or content of third-party services (Meta, OpenAI, payment processors)
- Your use of third-party services is governed by their respective terms and policies`
    },
    {
      id: 12,
      icon: <AlertCircle size={20} />,
      title: "12. Termination",
      content: `12.1 Termination by You:
- You may cancel your subscription at any time from account settings
- Cancellation takes effect at the end of the current billing cycle
- No refund is provided for the unused portion of the current billing period

12.2 Termination by Us:
We may suspend or terminate your account immediately if:
- You breach any provision of these Terms
- Your WhatsApp number is banned by Meta
- You engage in fraudulent or illegal activity
- Your account poses a security risk to other users
- We are required to do so by law or regulatory authority

12.3 Effect of Termination:
- Your access to the Platform will be revoked immediately
- Your WhatsApp integration will be deactivated
- Your data will be retained for 30 days then permanently deleted
- Outstanding payments remain due

12.4 Survival:
Provisions regarding intellectual property, limitation of liability, and governing law survive termination.`
    },
    {
      id: 13,
      icon: <Shield size={20} />,
      title: "13. Grievance Redressal (DPDP Act Mandatory)",
      content: `In accordance with the Information Technology Act, 2000 and the Digital Personal Data Protection Act, 2023, we have appointed a Grievance Officer.

13.1 Grievance Officer Details:
Name: ${grievanceName}
Email: ${grievanceEmail}
Address: ${companyAddress}
Working Hours: Monday to Friday, 10:00 AM to 6:00 PM IST

13.2 How to File a Grievance:
- Email your complaint with subject line "GRIEVANCE - [Your Account Email]"
- Include: your name, account email, nature of grievance, and supporting details
- We will acknowledge receipt within 24 hours
- Resolution will be provided within 15 business days

13.3 Data Protection Complaints:
- For DPDP Act related complaints, contact the Grievance Officer first
- If unsatisfied, you may approach the Data Protection Board of India once constituted
- For IT Act complaints, you may approach the adjudicating officer under IT Act, 2000

13.4 Consumer Disputes:
- For subscription/payment disputes, contact us at ${companyEmail}
- Unresolved disputes may be referred to the appropriate Consumer Forum under Consumer Protection Act, 2019`
    },
    {
      id: 14,
      icon: <FileText size={20} />,
      title: "14. Governing Law & Dispute Resolution",
      content: `14.1 Governing Law:
- These Terms are governed by the laws of India
- Applicable legislation includes: IT Act 2000, DPDP Act 2023, Consumer Protection Act 2019, Contract Act 1872

14.2 Dispute Resolution:
- Parties shall first attempt to resolve disputes through good-faith negotiation
- If negotiation fails within 30 days, disputes shall be referred to arbitration
- Arbitration shall be conducted under the Arbitration and Conciliation Act, 1996
- The seat of arbitration shall be [Your City], India
- The language of arbitration shall be English

14.3 Jurisdiction:
- Subject to the arbitration clause, courts at [Your City], India shall have exclusive jurisdiction
- You consent to the personal jurisdiction of such courts

14.4 Class Action Waiver:
- You agree to resolve disputes individually and not as part of a class action`
    },
    {
      id: 15,
      icon: <Mail size={20} />,
      title: "15. Contact Information",
      content: `For any questions, concerns, or notices regarding these Terms:

Company: ${companyName}
Email: ${companyEmail}
Grievance Officer: ${grievanceEmail}
Address: ${companyAddress}

For immediate support: Use the chat feature on our Platform
For billing queries: ${companyEmail} (Response within 24 hours)
For legal notices: Send to our registered address via registered post

These Terms and Conditions were last updated in ${lastUpdated}.`
    }
  ];

  return (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(160deg, #f0fdf9 0%, #e8f5e9 30%, #f8fafc 100%)',
      fontFamily: "'Plus Jakarta Sans', -apple-system, BlinkMacSystemFont, sans-serif",
    }}>
      {/* Header */}
      <div style={{
        background: 'linear-gradient(135deg, #075E54 0%, #128C7E 50%, #25D366 100%)',
        padding: '60px 24px 40px',
        textAlign: 'center',
        position: 'relative',
        overflow: 'hidden',
      }}>
        <div style={{
          position: 'absolute', top: '-40px', right: '-40px',
          width: '180px', height: '180px',
          background: 'rgba(255,255,255,0.06)', borderRadius: '50%',
        }} />
        <div style={{
          display: 'inline-flex', alignItems: 'center', gap: '10px',
          padding: '6px 18px',
          background: 'rgba(255,255,255,0.15)',
          borderRadius: '100px', marginBottom: '16px',
          border: '1px solid rgba(255,255,255,0.25)',
        }}>
          <Shield size={14} color="white" />
          <span style={{ color: 'white', fontSize: '13px', fontWeight: '600' }}>Legal Document</span>
        </div>
        <h1 style={{
          color: 'white', fontSize: '36px', fontWeight: '800',
          margin: '0 0 12px 0', letterSpacing: '-0.5px',
        }}>
          Terms & Conditions
        </h1>
        <p style={{ color: 'rgba(255,255,255,0.85)', fontSize: '15px', margin: '0 0 16px 0' }}>
          Please read these terms carefully before using ChatTech24
        </p>
        <div style={{
          display: 'inline-flex', alignItems: 'center', gap: '6px',
          padding: '5px 14px',
          background: 'rgba(255,255,255,0.12)',
          borderRadius: '8px',
          border: '1px solid rgba(255,255,255,0.2)',
        }}>
          <FileText size={13} color="rgba(255,255,255,0.8)" />
          <span style={{ color: 'rgba(255,255,255,0.8)', fontSize: '12px', fontWeight: '500' }}>
            Last Updated: {lastUpdated} &nbsp;|&nbsp; DPDP Act 2023 Compliant
          </span>
        </div>
      </div>

      {/* Quick Summary Cards */}
      <div style={{ maxWidth: '860px', margin: '0 auto', padding: '32px 24px 0' }}>
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
          gap: '16px', marginBottom: '32px',
        }}>
          {[
            { icon: <Shield size={20} color="#128C7E" />, title: 'DPDP Act 2023', desc: 'Fully compliant with India\'s data protection law', bg: '#f0fdf9', border: 'rgba(37,211,102,0.2)' },
            { icon: <Lock size={20} color="#3b82f6" />, title: 'Data Security', desc: 'SSL encryption & secure data storage', bg: '#eff6ff', border: '#bfdbfe' },
            { icon: <Users size={20} color="#7c3aed" />, title: 'Your Rights', desc: 'Access, correct, or delete your data anytime', bg: '#f5f3ff', border: '#ddd6fe' },
            { icon: <Mail size={20} color="#d97706" />, title: 'Grievance Officer', desc: '15-day resolution guarantee', bg: '#fffbeb', border: '#fde68a' },
          ].map((card, i) => (
            <div key={i} style={{
              background: card.bg,
              border: `1.5px solid ${card.border}`,
              borderRadius: '12px', padding: '16px',
            }}>
              <div style={{ marginBottom: '8px' }}>{card.icon}</div>
              <div style={{ fontSize: '13px', fontWeight: '700', color: '#1e293b', marginBottom: '4px' }}>{card.title}</div>
              <div style={{ fontSize: '12px', color: '#64748b', lineHeight: '1.5' }}>{card.desc}</div>
            </div>
          ))}
        </div>

        {/* Accordion Sections */}
        {sections.map((section) => (
          <div key={section.id} style={{
            background: 'white',
            borderRadius: '12px',
            marginBottom: '12px',
            border: '1.5px solid',
            borderColor: openSection === section.id ? 'rgba(37,211,102,0.4)' : '#e2e8f0',
            overflow: 'hidden',
            boxShadow: openSection === section.id ? '0 4px 16px rgba(37,211,102,0.1)' : '0 1px 4px rgba(0,0,0,0.06)',
            transition: 'all 0.2s ease',
          }}>
            <button
              onClick={() => toggle(section.id)}
              style={{
                width: '100%', padding: '18px 24px',
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                background: 'transparent', border: 'none', cursor: 'pointer',
                textAlign: 'left', gap: '12px',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <div style={{
                  width: '36px', height: '36px',
                  background: openSection === section.id
                    ? 'linear-gradient(135deg, #25D366, #128C7E)'
                    : '#f1f5f9',
                  borderRadius: '8px',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  color: openSection === section.id ? 'white' : '#64748b',
                  flexShrink: 0,
                  transition: 'all 0.2s',
                }}>
                  {section.icon}
                </div>
                <span style={{
                  fontSize: '15px', fontWeight: '700',
                  color: openSection === section.id ? '#065f46' : '#1e293b',
                }}>
                  {section.title}
                </span>
              </div>
              <div style={{ color: '#94a3b8', flexShrink: 0 }}>
                {openSection === section.id ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
              </div>
            </button>

            {openSection === section.id && (
              <div style={{
                padding: '0 24px 24px',
                borderTop: '1px solid #f1f5f9',
              }}>
                <div style={{ paddingTop: '16px' }}>
                  {section.content.split('\n').map((line, i) => (
                    <p key={i} style={{
                      fontSize: '14px',
                      color: line.startsWith('•') ? '#475569' : '#374151',
                      lineHeight: '1.75',
                      margin: '0 0 6px 0',
                      paddingLeft: line.startsWith('•') ? '8px' : '0',
                      fontWeight: line.endsWith(':') && !line.startsWith('•') ? '600' : '400',
                    }}>
                      {line}
                    </p>
                  ))}
                </div>
              </div>
            )}
          </div>
        ))}

        {/* Footer Note */}
        <div style={{
          marginTop: '32px', marginBottom: '48px',
          padding: '20px 24px',
          background: 'linear-gradient(135deg, #f0fdf9, #dcfce7)',
          borderRadius: '12px',
          border: '1.5px solid rgba(37,211,102,0.25)',
          display: 'flex', alignItems: 'flex-start', gap: '12px',
        }}>
          <Shield size={20} color="#128C7E" style={{ flexShrink: 0, marginTop: '2px' }} />
          <div>
            <p style={{ margin: '0 0 6px 0', fontSize: '14px', fontWeight: '700', color: '#065f46' }}>
              Questions about these Terms?
            </p>
            <p style={{ margin: 0, fontSize: '13px', color: '#047857', lineHeight: '1.6' }}>
              Contact us at <strong>{companyEmail}</strong> or reach our Grievance Officer at <strong>{grievanceEmail}</strong>. 
              We respond to all legal inquiries within 24 hours on business days.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

export default TermsAndConditions;